/**
 * @typedef {Object} LocalStorageServiceInterface
 * @property {(key: string, value: string) => void} saveItem - Saves an item to local storage
 * @property {(key: string) => string | null} getItem - Retrieves an item from local storage
 * @property {(key: string) => void} removeItem - Removes an item from local storage
 * @property {() => void} clear - Clears all items from local storage
 */

/**
 * LocalStorageService provides methods to interact with the browser's local storage.
 * All methods are synchronous and handle localStorage API internally.
 *
 * @returns {LocalStorageServiceInterface} An object containing methods to save, get, and remove items from local storage
 *
 * @example
 * const storage = LocalStorageService();
 * storage.saveItem('user', JSON.stringify({ name: 'John' }));
 * const user = JSON.parse(storage.getItem('user') || '{}');
 * storage.removeItem('user');
 */
export const LocalStorageService = () => {
  return {
    /**
     * Saves an item to local storage.
     *
     * @param {string} key - The key under which the value is stored
     * @param {string} value - The value to store (must be a string)
     * @throws {Error} When localStorage is not available or quota is exceeded
     *
     * @example
     * storage.saveItem('theme', 'dark');
     * storage.saveItem('settings', JSON.stringify({ lang: 'en' }));
     */
    saveItem: (key, value) => {
      window.localStorage.setItem(key, value);
    },
    /**
     * Retrieves an item from local storage.
     *
     * @param {string} key - The key of the item to retrieve
     * @returns {string | null} The retrieved item or null if not found
     *
     * @example
     * const theme = storage.getItem('theme'); // 'dark' or null
     * const settings = JSON.parse(storage.getItem('settings') || '{}');
     */
    getItem: (key) => {
      return window.localStorage.getItem(key);
    },
    /**
     * Removes an item from local storage.
     *
     * @param {string} key - The key of the item to remove
     *
     * @example
     * storage.removeItem('theme');
     * storage.removeItem('user-session');
     */
    removeItem: (key) => {
      window.localStorage.removeItem(key);
    },
    /**
     * Clears all items from local storage.
     * Warning: This will remove ALL localStorage data for the domain.
     *
     * @example
     * storage.clear(); // Removes everything from localStorage
     */
    clear: () => {
      window.localStorage.clear();
    },
  };
};
